/**
 * Year options for a "pick a calendar year" switcher (payroll runs, paystubs,
 * …), derived from the clock instead of a hardcoded list.
 *
 * A literal `["2024", "2025", "2026"]` silently stops offering the current year
 * the moment the calendar rolls over, and a literal default year strands users
 * on a stale view — so both are computed here.
 *
 * `now` is injectable so callers and tests stay deterministic.
 */
export const getSelectableYears = (
  firstYear: number,
  now: Date = new Date()
): string[] => {
  const currentYear = now.getFullYear();

  if (currentYear < firstYear) {
    return [`${firstYear}`];
  }

  return Array.from(
    { length: currentYear - firstYear + 1 },
    (_, index) => `${firstYear + index}`
  );
};

/** The year a switcher should land on by default: the current one. */
export const getCurrentSelectableYear = (
  firstYear: number,
  now: Date = new Date()
): string => `${Math.max(firstYear, now.getFullYear())}`;

/**
 * Inclusive UTC bounds for a calendar year, for a `gte`/`lte` date filter.
 *
 * The upper bound is the LAST instant of Dec 31 — bounding it at Dec 31 midnight
 * silently drops everything dated during that final day.
 */
export const getYearUtcRange = (year: string): { gte: Date; lte: Date } => ({
  gte: new Date(`${year}-01-01T00:00:00.000Z`),
  lte: new Date(`${year}-12-31T23:59:59.999Z`)
});
